"use client";
import { useCallback, useEffect, useRef, useState, useId } from "react";
import { InputText } from "@jetblue/kit/input-text";
import type { InputCityProperties } from ".";
import { IconButton } from "@jetblue/kit/icon-button";
import { IcChevronLeft } from "@jetblue/kit-icons/ic-chevron-left";
import { useVirtualizer } from "@tanstack/react-virtual";
import FocusLock from "react-focus-lock";
import { RemoveScroll } from "react-remove-scroll";
import { MobileListboxOption } from "./mobile-listbox-options";
import { useFilteredItems } from "./use-filtered-items";
import { useVisualViewport } from "./use-visual-viewport";
import { useFlightSearchInput } from "./use-flight-search-input";
import { useKeydown } from "./use-keydown";

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

export const InputCityMobile = ({
  labels,
  items = [],
  selectedKey,
  className,
  setSelectedKey,
  error,
  showValidation,
}: InputCityProperties) => {
  const modalInputReference = useRef<HTMLInputElement>(null);
  const scrollElementReference = useRef<HTMLInputElement>(null);
  const suppressNextFocusSwap = useRef(false);
  const suppressNextClear = useRef(false);
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useFlightSearchInput(
    items,
    selectedKey,
    isOpen,
  );
  const [activeIndex, setActiveIndex] = useState(0);
  const dialogTitleId = useId();
  const { height: viewportHeight } = useVisualViewport();
  const baseId = useId();
  const listboxId = `${baseId}-listbox`;
  const getOptionId = (code: string, index: number) =>
    `${baseId}-option-${code}-${index.toString()}`;

  const filteredItems = useFilteredItems(inputValue, items);

  const open = useCallback(() => {
    setIsOpen(true);
    requestAnimationFrame(() => {
      window.scrollTo(0, 0);
    });
  }, []);

  const close = useCallback(() => {
    if (!suppressNextClear.current && inputValue === "") {
      setSelectedKey(undefined);
    }
    suppressNextFocusSwap.current = true;
    setIsOpen(false);
    requestAnimationFrame(() => {
      suppressNextFocusSwap.current = false;
    });
  }, [setSelectedKey, inputValue]);
  const virtualizer = useVirtualizer({
    count: filteredItems.length,
    enabled: isOpen,
    estimateSize: () => 40,
    getScrollElement: () => scrollElementReference.current,
    overscan: 5,
  });
  const moveActive = useCallback(
    (delta: number) => {
      setActiveIndex((previous) => {
        const next = clamp(
          previous + delta,
          0,
          Math.max(0, filteredItems.length - 1),
        );
        virtualizer.scrollToIndex(next, { align: "auto" });
        return next;
      });
    },
    [filteredItems.length, virtualizer],
  );
  useEffect(() => {
    setActiveIndex(0);
    if (isOpen) {
      virtualizer.scrollToIndex(0);
    }
  }, [filteredItems, isOpen, virtualizer]);

  const selectIndex = useCallback(
    (index: number) => {
      suppressNextClear.current = true;
      requestAnimationFrame(() => {
        suppressNextClear.current = false;
      });
      const selected = filteredItems[index];
      if (!selected) {
        return;
      }

      setSelectedKey(selected.code);
      close();
    },
    [filteredItems, setSelectedKey, close],
  );
  useKeydown(
    useCallback(
      (event: KeyboardEvent) => {
        if (event.key === "Escape") {
          event.preventDefault();
          close();
        }
        if (event.key === "ArrowDown") {
          event.preventDefault();
          moveActive(1);
          return;
        }

        if (event.key === "ArrowUp") {
          event.preventDefault();
          moveActive(-1);
          return;
        }

        if (event.key === "Enter") {
          event.preventDefault();
          selectIndex(activeIndex);
        }
      },
      [close, moveActive, selectIndex, activeIndex],
    ),
    isOpen,
  );

  const activeOptionId = filteredItems[activeIndex]
    ? getOptionId(filteredItems[activeIndex].code, activeIndex)
    : undefined;
  return (
    <>
      <div className={className}>
        <InputText
          error={error}
          inputProperties={{
            onClick: () => {
              open();
            },
          }}
          isInvalid={showValidation ? !selectedKey : undefined}
          label={labels.input}
          onChange={(event) => {
            setInputValue(event.target.value);
          }}
          onFocus={() => {
            if (!suppressNextFocusSwap.current) {
              open();
            }
          }}
          onKeyDown={(event) => {
            if (event.key === "ArrowDown") {
              open();
            }
          }}
          value={inputValue}
        />
      </div>
      {isOpen && (
        <FocusLock>
          <div
            aria-labelledby={dialogTitleId}
            aria-modal="true"
            className="fixed inset-0 z-[99999] flex flex-col overflow-auto bg-white"
            role="dialog"
            style={{
              paddingBottom: viewportHeight
                ? `calc(100dvh - ${viewportHeight.toString()}px)`
                : 0,
            }}
          >
            <div aria-live="polite" className="sr-only">
              {filteredItems.length === 0
                ? "No results."
                : `${filteredItems.length.toString()} results available.`}
            </div>
            <header className="flex flex-row items-center gap-2 p-4 pl-2 shadow-jb-1">
              <h2 className="sr-only" id={dialogTitleId}>
                {labels.input}
              </h2>
              <IconButton
                aria-label={labels.close}
                className="min-w-12 border-none"
                onPress={() => {
                  close();
                }}
                size="medium"
                variant="standard"
              >
                <IcChevronLeft className="size-4" />
              </IconButton>
              <InputText
                data-autofocus-inside
                inputProperties={{
                  "aria-activedescendant": activeOptionId,
                  "aria-autocomplete": "list",
                  "aria-controls": listboxId,
                  "aria-expanded": true,
                  ref: modalInputReference,
                  role: "combobox",
                }}
                label={labels.input}
                onChange={(event) => {
                  setInputValue(event.target.value);
                }}
                selectOnFocus
                value={inputValue}
              />
            </header>
            <RemoveScroll forwardProps ref={scrollElementReference}>
              <div
                className="relative flex-1 overflow-auto"
                style={{
                  height: virtualizer.getTotalSize(),
                }}
                tabIndex={-1}
              >
                <ul
                  aria-label={labels.input}
                  className="absolute left-0 top-0 w-full"
                  id={listboxId}
                  role="listbox"
                  style={{
                    transform: `translateY(${virtualizer.getVirtualItems()[0]?.start.toString() ?? "0"}px)`,
                  }}
                >
                  {virtualizer.getVirtualItems().map((vItem) => {
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- safety: they are based on the same list
                    const { indent, code, name } = filteredItems[vItem.index]!;
                    return (
                      <MobileListboxOption
                        active={activeIndex === vItem.index}
                        code={code}
                        getOptionId={getOptionId}
                        indent={indent}
                        index={vItem.index}
                        inputValue={inputValue}
                        key={`${code}-${vItem.index.toString()}`}
                        name={name}
                        onActive={(index) => {
                          setActiveIndex(index);
                        }}
                        onSelect={selectIndex}
                        ref={virtualizer.measureElement}
                        selected={selectedKey === code}
                        setSize={filteredItems.length}
                      />
                    );
                  })}
                </ul>
              </div>
            </RemoveScroll>
          </div>
        </FocusLock>
      )}
    </>
  );
};
